import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FactoryDetailDto, FactoryDto, FactoryReportEntry } from "@audit-plan/shared";
import { api } from "./client";

export interface FactoryInput {
  code?: string;
  name: string;
  nameLocal?: string;
  address?: string;
  city?: string;
  country?: string;
  tier?: 2 | 3;
}

/** Stabilimenti (di un fornitore, o tutti con la ricerca). */
export function useFactories(q: { supplierId?: string; q?: string; includeInactive?: boolean }, enabled = true) {
  return useQuery({
    queryKey: ["factories", q],
    queryFn: async () => (await api.get<{ items: FactoryDto[] }>("/factories", q)).items,
    enabled,
  });
}

export function useFactory(id?: string | null) {
  return useQuery({ queryKey: ["factory", id], queryFn: () => api.get<FactoryDetailDto>(`/factories/${id}`), enabled: !!id });
}

/** Scheda «Report» dello stabilimento (AUD-F2.16). */
export function useFactoryReports(id?: string | null, enabled = true) {
  return useQuery({ queryKey: ["factory", id, "reports"], queryFn: () => api.get<FactoryReportEntry[]>(`/factories/${id}/reports`), enabled: !!id && enabled });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    for (const k of ["factories", "factory", "supplier", "suppliers", "audits", "plan"]) void qc.invalidateQueries({ queryKey: [k] });
  };
}

export function useSaveFactory() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, supplierId, body }: { id?: string; supplierId?: string; body: FactoryInput }) =>
      id ? api.patch<FactoryDto>(`/factories/${id}`, body) : api.post<FactoryDto>("/factories", { ...body, supplierId }),
    onSuccess: inv,
  });
}

export function useLinkFactory() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, supplierId, relationshipType }: { id: string; supplierId: string; relationshipType?: string }) => api.post<FactoryDto>(`/factories/${id}/suppliers`, { supplierId, relationshipType: relationshipType ?? "" }),
    onSuccess: inv,
  });
}

export function useUnlinkFactory() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, supplierId }: { id: string; supplierId: string }) => api.del<FactoryDto>(`/factories/${id}/suppliers/${supplierId}`),
    onSuccess: inv,
  });
}
